// The command palette's id lookups (spec/dashboard.md section 6.6, the support half): a player id or a payment id typed in
// `Ctrl+K` is looked up in every environment the caller may read. A player-shaped value (section 11.1's one-box order:
// uuid → user_id, digits → nutaku, a long hex fingerprint → device; the custom id as the second try) asks
// `GET …/envs/{env}/players?by=&value=` at most twice per environment; a payment-shaped value (a Nutaku payment id: a long
// run of digits, or a provider token such as `pay-1003`) asks `GET …/envs/{env}/orders?paymentId=` once per environment
// (section 11.5's one-call lookup). The environments answer in parallel; one that fails (a 403, a 404) adds nothing.
import type { ApiClient } from '../api/client';
import { ops } from '../api/operations';
import type { Environment, PaymentOrder, PermissionName, Player } from '../api/types';
import { guessIdKinds, type IdKind } from '../players/idKind';
import type { Command } from '../ui/CommandPalette';
import { relativeTime } from '../ui/time';
import { priceText } from '../pages/orders/orders';
import { routes } from './routes';

export interface IdSearchContext {
  client: ApiClient;
  org: string;
  game: string;
  environments: readonly Environment[];
  can: (permission: PermissionName, env?: string | null) => boolean;
  navigate: (to: string) => void;
}

/** The most id hits the palette lists. */
export const ID_RESULTS_CAP = 5;

/** The player lookups a value deserves (at most two), or none when it does not look like a player id. */
export function playerLookups(text: string): IdKind[] {
  const kinds = guessIdKinds(text);
  const first = kinds[0];
  if (first !== 'user_id' && first !== 'nutaku' && first !== 'device') return [];
  return kinds.slice(0, 2);
}

/** A Nutaku payment id (digits, six or more) or a provider token with a numeric tail (`pay-1003`). */
export function looksLikePaymentId(text: string): boolean {
  const v = text.trim();
  return /^\d{6,}$/.test(v) || /^[a-z]{1,10}[-_]\d{3,}$/i.test(v);
}

/** Whether the palette should treat the text as an id at all (the fallback "Find player…" command follows this). */
export function looksLikePlayerId(text: string): boolean {
  return playerLookups(text).length > 0;
}

/** The last 12 characters of a uuid (its random tail, section 11.1); any other id as is. */
export function shortId(id: string): string {
  return id.length === 36 ? id.slice(-12) : id;
}

/** The environments whose players and orders the caller may read (`players_read`, as the players and orders pages check). */
export function readableEnvironments(environments: readonly Environment[], can: IdSearchContext['can']): Environment[] {
  return environments.filter((e) => can('players_read', e.slug));
}

const isAbort = (e: unknown) => (e as Error)?.name === 'AbortError';

async function findPlayers(ctx: IdSearchContext, env: Environment, value: string, kinds: IdKind[], signal: AbortSignal): Promise<Player[]> {
  for (const by of kinds) {
    try {
      const page = (await ctx.client.data(ops.findPlayers, { path: { org: ctx.org, game: ctx.game, env: env.slug }, query: { by, value }, signal })) as unknown as { items?: Player[] };
      const items = page?.items ?? [];
      if (items.length > 0) return items;
    } catch (e) {
      if (isAbort(e)) throw e;
      return []; // a 403 or any other failure: this environment adds nothing, and its second lookup is not asked
    }
  }
  return [];
}

async function findOrders(ctx: IdSearchContext, env: Environment, paymentId: string, signal: AbortSignal): Promise<PaymentOrder[]> {
  try {
    const page = (await ctx.client.data(ops.listOrders, { path: { org: ctx.org, game: ctx.game, env: env.slug }, query: { paymentId, limit: ID_RESULTS_CAP }, signal })) as unknown as { items?: PaymentOrder[] };
    return page?.items ?? [];
  } catch (e) {
    if (isAbort(e)) throw e;
    return [];
  }
}

/**
 * The palette's id results for a typed text: players (group "Players") then orders (group "Orders"), at most five in all.
 * Empty when the text looks like neither a player id nor a payment id, or no readable environment knows it.
 */
export async function searchIds(text: string, ctx: IdSearchContext, signal: AbortSignal): Promise<Command[]> {
  const value = text.trim();
  const kinds = playerLookups(value);
  const payment = looksLikePaymentId(value);
  if (kinds.length === 0 && !payment) return [];
  const envs = readableEnvironments(ctx.environments, ctx.can);
  if (envs.length === 0) return [];

  const [players, orders] = await Promise.all([
    kinds.length > 0 ? Promise.all(envs.map((env) => findPlayers(ctx, env, value, kinds, signal).then((items) => items.map((p) => ({ env, p }))))) : Promise.resolve([]),
    payment ? Promise.all(envs.map((env) => findOrders(ctx, env, value, signal).then((items) => items.map((o) => ({ env, o }))))) : Promise.resolve([]),
  ]);

  const commands: Command[] = [];
  for (const { env, p } of players.flat()) {
    commands.push({
      id: `player:${env.slug}:${p.id}`,
      label: `Player ${shortId(p.id)} · ${env.name}`,
      group: 'Players',
      hint: p.lastSeenAt ? `last seen ${relativeTime(p.lastSeenAt)}` : 'never seen',
      run: () => ctx.navigate(routes.player(ctx.org, ctx.game, env.slug, p.id)),
    });
  }
  for (const { env, o } of orders.flat()) {
    commands.push({
      id: `order:${env.slug}:${o.id}`,
      label: `Order ${o.paymentId} · ${env.name}`,
      group: 'Orders',
      hint: `${o.status} · ${priceText(o)}`,
      run: () => ctx.navigate(`${routes.orders(ctx.org, ctx.game, env.slug, { paymentId: o.paymentId })}&order=${encodeURIComponent(o.id)}`),
    });
  }
  return commands.slice(0, ID_RESULTS_CAP);
}

/** The environment the "Find player…" fallback opens: the first readable development environment, else the first readable. */
export function lookupEnvironment(environments: readonly Environment[], can: IdSearchContext['can']): Environment | null {
  const envs = readableEnvironments(environments, can);
  return envs.find((e) => e.kind === 'dev') ?? envs[0] ?? null;
}
